import { useEffect, useMemo, useState } from 'react';
import { useTable } from 'spacetimedb/react';
import { tables } from '@vidmakr/bindings';
import { roleAtLeast, type Role } from '@vidmakr/shared';
import { useSession } from './session';

export function useAccount() {
  const [rows, ready] = useTable(tables.account);
  return { account: rows[0] ?? null, ready };
}

export function useOrgs() {
  const [orgs, orgsReady] = useTable(tables.org);
  const [members, membersReady] = useTable(tables.orgMember);
  const userId = useSession((s) => s.user?.userId);
  const roles = useMemo(() => {
    const m = new Map<string, string>();
    for (const row of members) if (row.userId === userId) m.set(row.orgId, row.role);
    return m;
  }, [members, userId]);
  const sorted = useMemo(() => [...orgs].sort((a, b) => a.name.localeCompare(b.name)), [orgs]);
  return { orgs: sorted, members, roles, ready: orgsReady && membersReady };
}

export function useOrg(orgId: string | undefined) {
  const { orgs, roles, members, ready } = useOrgs();
  const org = orgs.find((o) => o.id === orgId) ?? null;
  const role = (orgId && roles.get(orgId)) || null;
  const orgMembers = useMemo(() => members.filter((m) => m.orgId === orgId), [members, orgId]);
  return {
    org,
    role,
    members: orgMembers,
    ready,
    can: (min: Role) => roleAtLeast(role, min),
  };
}

const LAST_ORG = 'vm:lastOrg';
export function rememberOrg(orgId: string) {
  try {
    localStorage.setItem(LAST_ORG, orgId);
  } catch {
    /* ignore */
  }
}
export function lastOrg(): string | null {
  try {
    return localStorage.getItem(LAST_ORG);
  } catch {
    return null;
  }
}

/** Uploaded logos of organizations and projects the viewer can see, by org/project id. */
export function useLogos(): Map<string, string> {
  const [rows] = useTable(tables.logo);
  return useMemo(() => new Map(rows.map((r) => [r.id, r.url])), [rows]);
}

/** Tracks a CSS media query (e.g. '(min-width: 1024px)'). */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setMatches(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [query]);
  return matches;
}

/** The window's inner size, updated as it is resized. */
export function useWindowSize(): { width: number; height: number } {
  const [size, setSize] = useState(() => ({ width: window.innerWidth, height: window.innerHeight }));
  useEffect(() => {
    const on = () => setSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return size;
}

/** An organization's colour palettes, by name. */
export function useOrgPalettes(orgId: string | undefined) {
  const [rows] = useTable(tables.colorPalette);
  return useMemo(() => rows.filter((r) => r.orgId === orgId).sort((a, b) => a.name.localeCompare(b.name)), [rows, orgId]);
}
